import { useMemo, useState } from "react";
import type { LineItem } from "@/interfaces/estimate-detail";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { useBatchUpdateLineItemsMutation } from "@/hooks/mutation/estimates/useBatchUpdateLineItemsMutation";
import { useSimilarIssuesIndex } from "../hooks/useSimilarIssues";
import { Line } from "./Line";
import { getConfidence } from "./util/get-confidence";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { useDispatch } from "@/redux/store";

interface Props {
  estimateId: string;
  pageId: string;
  lineItem: LineItem;
  tupleId: string;
}

export function SimilarIssues({ estimateId, pageId, lineItem, tupleId }: Props) {
  const dispatch = useDispatch();
  const { rooms, selectLineItem, deleteLineItem } = useEstimateDetail(estimateId);
  const { mutateAsync: batchUpdateLineItems } = useBatchUpdateLineItemsMutation();
  const similar = useSimilarIssuesIndex(rooms);
  const [open, setOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const key = useMemo(() => similar.keyFromItem(lineItem), [similar, lineItem]);
  const matches = similar.getSimilarRooms(key, pageId);
  const count = matches.length;

  if (count === 0) return null;

  return (
    <div className="my-1 w-full rounded-md border-2 border-gray-200 bg-slate-50">
      <button className="flex w-full items-center justify-between px-3 py-2 text-sm" onClick={() => setOpen((o) => !o)}>
        <span className="flex items-center gap-2 text-sm text-gray-800">
          Similar issues found in {count} other room{count > 1 ? "s" : ""}
        </span>
        <span className="text-gray-500">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div className="mt-2 space-y-3">
          {matches.map((m) => (
            <div key={m.pageId} className="mx-3 rounded-md border border-gray-200 bg-white">
              {m.items.map((it, idx) => {
                const confidence = getConfidence(it.lineItem, it.tuple);
                return (
                  <Line
                    key={`${m.pageId}:${it.tuple.tupleId}:${it.lineItem.lineItemId}:${idx}`}
                    order={idx + 1}
                    estimateId={estimateId}
                    pageId={m.pageId}
                    roomName={m.roomName}
                    tupleId={it.tuple.tupleId}
                    lineItem={it.lineItem}
                    confidence={confidence}
                    tuple={it.tuple}
                    isFreeFormFlag={it.tuple.isFreeFormFlag ?? false}
                    selectedTab={undefined}
                    showConfirm={false}
                    className="rounded-md !border-t-0"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await deleteLineItem(it.tuple.tupleId, m.pageId, it.lineItem.uniqueId, "issues");
                    }}
                    onSelect={() => selectLineItem(it.lineItem.uniqueId, "issues")}
                    showOrder={false}
                  />
                );
              })}
            </div>
          ))}

          <div className="flex justify-end gap-3 p-3">
            <Button
              variant="ghost"
              disabled={isConfirming || isRemoving}
              onClick={async () => {
                setIsConfirming(true);
                try {
                  const operations = [
                    { pageId, tupleId, lineItemId: lineItem.lineItemId, action: "confirm" as const },
                    ...matches.flatMap((m) =>
                      m.items.map((it) => ({
                        pageId: m.pageId,
                        tupleId: it.tuple.tupleId,
                        lineItemId: it.lineItem.lineItemId,
                        action: "confirm" as const,
                      })),
                    ),
                  ];

                  const { modifiedState } = await batchUpdateLineItems({ estimateId, operations });
                  dispatch(estimationActions.setEstimationState({ estimationDetail: modifiedState }));
                  toast.success(`Successfully confirmed ${operations.length} issues`);
                  setOpen(false);
                } catch (error) {
                  console.error("Error confirming issues:", error);
                  toast.error("An unexpected error occurred");
                } finally {
                  setIsConfirming(false);
                }
              }}
            >
              {isConfirming ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Confirming...
                </div>
              ) : (
                `Confirm All (${matches.flatMap((m) => m.items).length + 1} issue${matches.flatMap((m) => m.items).length + 1 > 1 ? "s" : ""})`
              )}
            </Button>
            <Button
              variant="destructive"
              disabled={isRemoving || isConfirming}
              onClick={async () => {
                setIsRemoving(true);
                try {
                  const operations = [
                    { pageId, tupleId, lineItemId: lineItem.lineItemId, action: "remove" as const },
                    ...matches.flatMap((m) =>
                      m.items.map((it) => ({
                        pageId: m.pageId,
                        tupleId: it.tuple.tupleId,
                        lineItemId: it.lineItem.lineItemId,
                        action: "remove" as const,
                      })),
                    ),
                  ];

                  const { modifiedState } = await batchUpdateLineItems({ estimateId, operations });
                  dispatch(estimationActions.setEstimationState({ estimationDetail: modifiedState }));
                  toast.success(`Successfully removed ${operations.length} issues`);
                  setOpen(false);
                } catch (error) {
                  console.error("Error removing issues:", error);
                  toast.error("An unexpected error occurred");
                } finally {
                  setIsRemoving(false);
                }
              }}
            >
              {isRemoving ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Removing...
                </div>
              ) : (
                `Discard All (${matches.flatMap((m) => m.items).length + 1} issue${matches.flatMap((m) => m.items).length + 1 > 1 ? "s" : ""})`
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
